import {ErrorHandler, Injectable, NgModule} from '@angular/core';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {FlexLayoutModule} from '@angular/flex-layout';

import {NgxWebstorageModule} from 'ngx-webstorage';
import {TranslateModule, TranslateLoader} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

import {RECAPTCHA_LANGUAGE} from 'ng-recaptcha';

import {TokenInterceptorService} from './utils/interceptors/token-interceptor/token-interceptor.service';
import {AuthInterceptorService} from './utils/interceptors/auth-interceptor/auth-interceptor.service';

import {TokenService} from './services/local/token/token.service';
import {SessionModule} from './services/local/session/session.service';
import {TitleService} from './services/local/title/title.service';

import {RequestModule} from './services/api/request/request.service';
import {AuthService} from './services/api/auth/auth.service';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LinGuard} from './utils/guards/lin-guard/lin.guard';
import {AuthGuard} from './utils/guards/auth-guard/auth.guard';
import {FileService} from './services/api/file/file.service';
import {BroadcasterModule} from './services/local/broadcaster/broadcaster.service';
import {WebsocketModule} from './services/api/websocket/websocket.service';
import {SnackBarMessageService} from './services/local/snack-bar-message/snack-bar-message.service';
import {LinaLoadingComponent} from './shared/lina-loading/lina-loading.component';
import {MatLegacyDialogModule as MatDialogModule} from '@angular/material/legacy-dialog';
import {MatIconRegistry} from '@angular/material/icon';
import {MatLegacySnackBarModule as MatSnackBarModule} from '@angular/material/legacy-snack-bar';
import {CompatibilityGuard} from './utils/guards/compatibility-guard/compatibility.guard';
import {LinaGenericDialogsModule} from './shared/lina-generic-dialogs/lina-generic-dialogs.component';
import {Angulartics2Module} from 'angulartics2';
import {LoadingBarModule} from '@ngx-loading-bar/core';
import {LoadingBarHttpClientModule} from '@ngx-loading-bar/http-client';
import {LinaUiModule} from './shared/lina-ui/lina-ui.module';
import {GlobalErrorHandler} from './utils/handlers/global-error-handler/global-error.handler';
import {ErrorHandlerService} from './services/local/error-handler/error-handler.service';
import {SlackService} from './services/api/slack/slack.service';
import {OrderByPipe} from 'ngx-pipes';

import {BrowserCacheLocation, InteractionType, PublicClientApplication} from '@azure/msal-browser';
import {MsalModule, MsalRedirectComponent} from '@azure/msal-angular';
import { environment } from '../environments/environment';

import { MatLegacyPaginatorIntl as MatPaginatorIntl } from '@angular/material/legacy-paginator';
import { getSwedishPaginatorIntl } from './swedish-paginator-intl';
import { TranslateService } from '@ngx-translate/core';
import { NgxEditorModule } from 'ngx-editor';

// Ngx-translate
export function customTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/*@Injectable()
export class HammerConfig extends GestureConfig {
  buildHammer(element: HTMLElement) {
    return new GestureConfig({touchAction: 'pan-y'}).buildHammer(element);
  }
}*/

@NgModule({
  imports: [
    NgxEditorModule.forRoot({      
      icons: {
        bold: '<span class="material-icons">format_bold</span>',
        italic: '<span class="material-icons">format_italic</span>',
        align_left: '<span class="material-icons">format_align_left</span>',
        align_center: '<span class="material-icons">format_align_center</span>',
        align_right: '<span class="material-icons">format_align_right</span>',
        align_justify: '<span class="material-icons">format_align_justify</span>',
        bullet_list: '<span class="material-icons">format_list_bulleted</span>',
        color_fill: '<span class="material-icons">format_color_fill</span>',
        format_clear: '<span class="material-icons">format_clear</span>',      
        image: '<span class="material-icons">image</span>',
        link: '<span class="material-icons">link</span>',
        ordered_list: '<span class="material-icons">format_list_numbered</span>',
        quote: '<span class="material-icons">format_quote</span>',
        strike: '<span class="material-icons">format_strikethrough</span>',
        text_color: '<span class="material-icons">format_color_text</span>',
        underline: '<span class="material-icons">format_underlined</span>',
        unlink: '<span class="material-icons">link_off</span>'
      },
    }),
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FlexLayoutModule,
    NgxWebstorageModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (customTranslateLoader),
        deps: [HttpClient]
      }
    }),
    AppRoutingModule,
    LoadingBarModule,
    LoadingBarHttpClientModule,
    WebsocketModule.forRoot(),
    SessionModule.forRoot(),
    MatSnackBarModule,
    MatDialogModule,
    LinaGenericDialogsModule,
    Angulartics2Module.forRoot({
      pageTracking: {
        clearQueryParams: true,
        clearIds: true
      }
    }),
    LinaUiModule,
    BroadcasterModule.forRoot(),
    RequestModule.forRoot(),
    MsalModule.forRoot( new PublicClientApplication({
      auth: {
        clientId: environment.msalConfig.auth.clientId,
        authority: environment.msalConfig.auth.authority,
        knownAuthorities: environment.msalConfig.auth.knownAuthorities,
        redirectUri: environment.msalConfig.auth.redirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: false, // Set to true for Internet Explorer 11
      }
    }), 
    {
      interactionType: InteractionType.Redirect,
      authRequest: {
        scopes: ['user.read']
      }
    },
    null)
  ],
  providers: [
/*    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },*/
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true
    },
  /*{
      provide: HAMMER_GESTURE_CONFIG, useClass: HammerConfig
    },*/
    {
      provide: RECAPTCHA_LANGUAGE,
      useValue: 'sv',
    },
    // For translation paginator
    TranslateService,
    {
      provide: MatPaginatorIntl,
      useFactory: (translateService: TranslateService) => {
        if (translateService.currentLang === 'sv') {
          return getSwedishPaginatorIntl();
        }

        return new MatPaginatorIntl();
      },
      deps: [TranslateService]
    },
    TokenService,
    AuthService,
    AuthGuard,
    LinGuard,
    TitleService,
    FileService,
    SnackBarMessageService,
    CompatibilityGuard,
    ErrorHandlerService,
    SlackService,
    OrderByPipe
  ],
  declarations: [
    AppComponent,
    LinaLoadingComponent
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {

  constructor(
    private iconReg: MatIconRegistry,
    private sanitizer: DomSanitizer) {

    this.iconReg.addSvgIcon('recycle', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/recycle.svg'))
      .addSvgIcon('apple', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/apple.svg'))
      .addSvgIcon('qrcode', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/qrcode.svg'))
      .addSvgIcon('abc', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/alphabetical.svg'))
      .addSvgIcon('letter_s', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/alpha-s-circle-outline.svg'))
      .addSvgIcon('computer_lock', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/monitor-lock.svg'))
      .addSvgIcon('classroom', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/google-classroom.svg'))
      .addSvgIcon('facebook', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/facebook-box.svg'))
      .addSvgIcon('twitter', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/twitter-circle.svg'))
      .addSvgIcon('instagram', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/instagram.svg'))
      .addSvgIcon('linkedin', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/linkedin-box.svg'))
      .addSvgIcon('box', this.sanitizer.bypassSecurityTrustResourceUrl('../assets/icons/box.svg'));
  }
}
